'use client'

/**
 * HOME FEATURED_PRODUCTS_SHOWCASE PL widget — widget-of-widget container.
 *
 * v2 nested pattern (see PRODUCT_OVERVIEW / the home IRL pair): the page renderer pre-renders the
 * nested SINGLE_PRODUCT_CARD children and hands them in via `renderedChildren`; this container
 * decides how to arrange them. Like the non-PL `BackToSchool` section it branches on
 * `shouldUseDesignSystem()`: the DS chrome reuses `back-to-school-ds-widget`'s exported classes
 * (shell / header / 6-up grid + recommendation test ids), the legacy chrome mirrors
 * `BackToSchoolLegacy` (same scss module, header row + shop button / mobile arrow link, 6-col
 * grid). List-level concerns are shared by both branches — the device-specific product cap, the
 * section trackers, visibility + batched product-view analytics, the header CTA tracking. Each
 * child is the SHARED `SingleProductCardWidget` leaf (same one PDP/CLP register): it renders
 * itself from its own `productItem` data with DS/legacy routing built in, so the container only
 * arranges the pre-rendered nodes inside the grid.
 *
 * Section-level proto → domain conversion is delegated to `buildFeaturedProductsShowcaseConfig`
 * (heading/buttons/limits/analytics), keeping this container free of proto parsing.
 */

import { WidgetType } from '@lastbrand/storefront-proto-registry/api/v1/enums'
import { Button as DSButton } from '@lastbrand/web-ui/button'
import { ChevronRightIcon } from '@lastbrand/web-ui/icons'
import classnames from 'classnames'
import { useTranslation } from 'next-i18next'
import { useCallback, useMemo } from 'react'

import { RichTextDS } from '@src/components/blocks/rich-text-ds'
import {
  classnameArraysToDSStyle,
  dynamicValueClassesToInlineStyle,
} from '@src/components/blocks/rich-text-ds/classname-to-ds-style'
import { PLBaseErrorBoundary } from '@src/components/pl-base-error-boundary'
import { PLTypedWidgetInterface, PLTypedWidgetProps } from '@src/components/pl-base-widget'
import {
  chevronLinkClass,
  DEFAULT_PRODUCTS_LIMIT_FOR_DESKTOP,
  DEFAULT_PRODUCTS_LIMIT_FOR_MOBILE,
  gridClass,
  headerRowClass,
  plainTextFromDocument,
  sectionInnerClass,
  sectionShellClass,
  titleSlotClass,
} from '@src/components/widgets/back-to-school/back-to-school-ds-widget'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { Button, Icon, Link, RichText } from '@src/quince-components/src'
import TrackedLink from '@src/quince-components/src/atoms/link'
import ButtonDomain from '@src/quince-site-common/src/domains/Button'
import RichTextDomain from '@src/quince-site-common/src/domains/RichText'
import { useBatchedAnalyticsForProductCard } from '@src/quince-site-common/src/hooks/useBatchedAnalyticsForProductCard'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useDynamicStyle from '@src/quince-site-common/src/hooks/useDynamicStyle'
import { useTrackIsElementVisible } from '@src/quince-site-common/src/hooks/useTrackIsElementVisible'
import RightArrowIcon from '@src/quince-site-integrated/src/assets/right-carousel-arrow.svg'
import styles from '@src/SectionRenderer/sections/backToSchool/backToSchool.module.scss'
import productTileStyles from '@src/SectionRenderer/sections/commonStyles/newHomePageProductTile.module.scss'
import {
  getContentContainerTrackingMetadata,
  getTrackingContext,
} from '@src/SectionRenderer/sections/productListSection/productListSection.helpers'
import { FINDING_METHOD_AGGREGATE, getFindingMethodAggregate, setFindingMethod } from '@src/telemetry/findingMethod'
import { shouldUseDesignSystem } from '@src/utils/should-use-design-system'

import { CardDisplayOptionsProvider, type CardChromeProps } from '../single-product-card/card-display-options-context'
import { CardTrackingProvider } from '../single-product-card/card-tracking-context'

import { buildFeaturedProductsShowcaseConfig } from './featured-products-showcase-section-builders'

import type {
  FeaturedProductsShowcaseWidgetData,
  ProductCardData,
} from '@lastbrand/storefront-proto-registry/api/v1/widget'

// @i18n-ignore
const NAVIGATION = 'navigation'
// @i18n-ignore
const CLICK_SHOP_BUTTON = 'click__shop__button'

// Legacy-card tile styling for the 6-up home grid — mirrors the non-PL sections
// (BackToSchool / personalisedProducts) which pass these `newHomePageProductTile`
// classes to the shared `ProductCard`. The DS card sizes itself via `dsVariant`, but
// on the legacy path `dsVariant` is a no-op, so without these the legacy card falls
// back to its wider default typography and default heart position (larger text +
// misplaced wishlist icon vs the BFF render). Published on the leaf via `cardProps` on legacy
// renders only — the SVD DS card would render `productPriceClass` into its price DOM.
const HOME_TILE_CHROME_PROPS: CardChromeProps = {
  titleClassName: productTileStyles.title,
  productPriceClass: productTileStyles.price,
  wishlistIconClassName: productTileStyles.wishlistWrapper,
  wishlistIconClass: productTileStyles.wishlistIcon,
  ratingTextClassName: productTileStyles.ratingText,
  excessOptionsTextClassName: productTileStyles.excessOptionsText,
}

// Widget.widgets is a recursive proto type TS can't resolve through Omit<Widget,'data'>;
// read nested SINGLE_PRODUCT_CARD data via a narrow cast (same pattern as the home IRL widget).
interface ProductNestedWidget {
  data?: { productItem?: ProductCardData }
}

function FeaturedProductsShowcaseWidgetComponent(props: PLTypedWidgetProps<FeaturedProductsShowcaseWidgetData>) {
  const data = props.data.featuredProductsShowcaseData

  const { t } = useTranslation('common')
  const deviceInfo = useDeviceInfo()
  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP
  const isDesignSystem = shouldUseDesignSystem()

  const nestedWidgets = (props as unknown as { widgets?: ProductNestedWidget[] }).widgets
  const renderedChildren = props.renderedChildren

  // Section-level proto → domain values (heading, CTA buttons, caps, analytics) via the shared
  // builder — product cards render as pre-rendered children, so none are passed here.
  const sectionConfig = useMemo(() => (data ? buildFeaturedProductsShowcaseConfig(data, []) : null), [data])
  const {
    richTexts = [],
    classNamesMobileOverrides,
    classNames,
    mobileDynamicClassesOverrides,
    baseDynamicClasses,
    tracker: _tracker,
    buttons = [],
    id,
    sectionType,
    trackingMetaData = {},
    numberOfProductsToShowInDesktop = DEFAULT_PRODUCTS_LIMIT_FOR_DESKTOP,
    numberOfProductsToShowInMobile = DEFAULT_PRODUCTS_LIMIT_FOR_MOBILE,
  } = sectionConfig ?? {}

  // Product data paired 1:1 with renderedChildren by original index (a nested widget without
  // productItem contributes no slide), then capped to the device limit like the non-PL section.
  const cardEntries = useMemo(
    () =>
      (Array.isArray(nestedWidgets) ? nestedWidgets : []).flatMap((widget, index) => {
        const product = widget.data?.productItem
        const node = renderedChildren?.[index]?.node
        return product && node ? [{ product, node }] : []
      }),
    [nestedWidgets, renderedChildren],
  )
  const PRODUCTS_LIMIT = isDesktop ? numberOfProductsToShowInDesktop : numberOfProductsToShowInMobile
  const visibleEntries = useMemo(() => cardEntries.slice(0, PRODUCTS_LIMIT), [cardEntries, PRODUCTS_LIMIT])

  const { styleObject, className } = useDynamicStyle({
    dynamicValueClassesMobileOverride: mobileDynamicClassesOverrides,
    baseDynamicValueClasses: baseDynamicClasses,
    mobileClassNamesOverride: classNamesMobileOverrides,
    baseClassNames: classNames,
    postFix: styles.dynamicClassesPostFix ?? '',
    wrapperClassName: styles.dynamic_class_wrapper,
  })

  const productViewTracker = useMemo(() => {
    if (!_tracker) return undefined
    const context = trackingMetaData.context
    // Truthiness (`||`) on purpose, matching the non-PL forks: an empty-string sectionType falls
    // through to `label` (CustomSection types sectionType as `CustomSectionType | ''`).
    const sectionSuffix =
      context && (Boolean(context.sectionType) || Boolean(context.label))
        ? `__${context.sectionType}__${context.label}`
        : ''
    return `${_tracker}${sectionSuffix}__product_carousel`
  }, [_tracker, trackingMetaData])

  const contentContainerTrackingMetadata = useMemo(
    () => getContentContainerTrackingMetadata(trackingMetaData),
    [trackingMetaData],
  )

  const getIsVisibleTrackParams = useCallback(
    () => ({
      ...trackingMetaData,
    }),
    [trackingMetaData],
  )
  const { ref: trackIsVisibleRef } = useTrackIsElementVisible(getIsVisibleTrackParams)

  useBatchedAnalyticsForProductCard({
    productViewTracker,
    trackingMetaData,
  })

  // Each visible pre-rendered SINGLE_PRODUCT_CARD node becomes a grid cell, wrapped in its own
  // CardTrackingProvider carrying the EXACT per-card click/wishlist configs the non-PL section
  // builds inside `productCards.map(...)` — `ProductCard` emits PRODUCT_CLICK / sets
  // findingMethod / adds link attribution only when these are present, so without them the home
  // cards would go silent. The provider also carries the section view tracker (so impressions
  // land on this section's aggregator, not the page one). A single CardDisplayOptionsProvider
  // around the grid supplies the 6-up-grid `defaultSM` DS variant; display flags keep their
  // show-everything defaults (matches non-PL). Memoized so an unrelated context update doesn't
  // rebuild every card's provider value; `findingMethod` is consequently snapshotted per memo run
  // rather than per render — same value at click time in practice, since navigation that changes
  // it also re-renders the page.
  const slides = useMemo(
    () => (
      // eslint-disable-next-line i18next/no-literal-string -- DS tile variant token, not user-facing copy
      <CardDisplayOptionsProvider dsVariant="defaultSM" cardProps={isDesignSystem ? undefined : HOME_TILE_CHROME_PROPS}>
        {visibleEntries.map((entry, idx) => {
          const tracker = `${_tracker}__click_product_carousel`
          const trackingContext = getTrackingContext(tracker, entry.product.slug ?? '')
          const productClickTrackConfig = {
            trackInternal: true,
            trackGA: true,
            ...trackingContext,
            tracker,
            context: {
              sectionType,
              sectionid: id,
              ...trackingMetaData.context,
              productPosition: idx,
              ...contentContainerTrackingMetadata,
              findingMethod: getFindingMethodAggregate(),
            },
          }
          const wishlistTrackConfig = {
            ...trackingContext,
            tracker,
            sectionType,
            sectionid: id,
            ...trackingMetaData.context,
            productPosition: idx,
            ...contentContainerTrackingMetadata,
            findingMethod: getFindingMethodAggregate(),
          }
          return (
            <CardTrackingProvider
              key={String(entry.product.productId)}
              tracker={tracker}
              productClickTrackConfig={productClickTrackConfig}
              wishlistTrackConfig={wishlistTrackConfig}
              productViewTracker={productViewTracker}
            >
              {entry.node}
            </CardTrackingProvider>
          )
        })}
      </CardDisplayOptionsProvider>
    ),
    [
      visibleEntries,
      _tracker,
      sectionType,
      id,
      trackingMetaData,
      contentContainerTrackingMetadata,
      productViewTracker,
      isDesignSystem,
    ],
  )

  if (!data || !sectionConfig) return null
  if (!visibleEntries.length) return null

  const headerButtonProps = ButtonDomain.makeButtonProps(buttons[0])
  const headerTrackConfig = headerButtonProps && {
    trackInternal: true,
    category: NAVIGATION,
    action: CLICK_SHOP_BUTTON,
    label: headerButtonProps.children as string,
    value: `${_tracker}__product_carousel`,
    context: { ...trackingMetaData.context },
  }
  const onHeaderButtonClick = () => {
    setFindingMethod[FINDING_METHOD_AGGREGATE.CAMPAIGN]?.({
      elementName: trackingMetaData.context?.contentContainerName ?? '',
      elementId: trackingMetaData.context?.trackingId ?? '',
    })
  }

  const headingRichTextProps = RichTextDomain.makeRichTextProps(richTexts[0])
  const headingDynamic = headingRichTextProps
    ? dynamicValueClassesToInlineStyle(
        headingRichTextProps.baseDynamicValueClasses,
        headingRichTextProps.dynamicValueClassesMobile,
      )
    : undefined

  // DS branch — mirrors BackToSchoolDSWidget (same exported classes, header CTA, 6-up grid,
  // recommendation test ids), arranging pre-rendered children instead of mapping data to cards.
  if (isDesignSystem) {
    const LinkWithTracking = (linkProps: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
      <TrackedLink {...linkProps} trackConfig={headerTrackConfig} withoutBaseStyle hidePointerCursor />
    )

    const dsHeaderButton = headerButtonProps ? (
      isDesktop ? (
        <DSButton
          // eslint-disable-next-line i18next/no-literal-string -- DS Button variant token, not user-facing copy
          variant="tertiary"
          // eslint-disable-next-line i18next/no-literal-string -- DS Button size token, not user-facing copy
          size="sm"
          id={TEST_LOCATORS.BACK_TO_SCHOOL.HEADER_BUTTON}
          LinkComponent={LinkWithTracking}
          href={headerButtonProps.href}
          onClick={onHeaderButtonClick as React.MouseEventHandler<HTMLAnchorElement>}
        >
          {headerButtonProps.children}
        </DSButton>
      ) : (
        <LinkWithTracking
          id={TEST_LOCATORS.BACK_TO_SCHOOL.HEADER_BUTTON}
          href={headerButtonProps.href}
          onClick={onHeaderButtonClick as React.MouseEventHandler<HTMLAnchorElement>}
          aria-label={t('shop_now_v1')}
          className={chevronLinkClass}
        >
          <ChevronRightIcon width="24px" height="24px" aria-hidden />
        </LinkWithTracking>
      )
    ) : null

    return (
      <div ref={trackIsVisibleRef} className={sectionShellClass}>
        <div className={sectionInnerClass}>
          <div className={headerRowClass}>
            <div
              className={titleSlotClass}
              data-testid={TEST_LOCATORS.BACK_TO_SCHOOL.TITLE(plainTextFromDocument(richTexts[0]?.raw))}
            >
              {headingRichTextProps?.content ? (
                <RichTextDS
                  content={headingRichTextProps.content}
                  baseStyle={classnameArraysToDSStyle(
                    headingRichTextProps.commonClassNames,
                    headingRichTextProps.mobileClassNames,
                  )}
                  className={headingDynamic?.className}
                  style={headingDynamic?.style}
                />
              ) : null}
            </div>
            {dsHeaderButton}
          </div>
          <div className={gridClass}>{slides}</div>
        </div>
      </div>
    )
  }

  // Legacy branch — mirrors BackToSchoolLegacy's chrome.
  const legacyHeaderButton = headerButtonProps ? (
    isDesktop ? (
      <Button
        {...headerButtonProps}
        className={classnames(headerButtonProps.className, styles.shopButton)}
        trackConfig={headerTrackConfig}
        onClick={onHeaderButtonClick}
      />
    ) : (
      <Link
        href={headerButtonProps.href}
        className={styles.arrowLinkContianer}
        trackConfig={headerTrackConfig}
        onClick={onHeaderButtonClick}
        aria-label={t('shop_now_v1')}
      >
        <Icon iconSvg={RightArrowIcon} className={styles.arrow} />
      </Link>
    )
  ) : null

  return (
    <div ref={trackIsVisibleRef}>
      <div className={classnames(styles.container, className)} style={styleObject}>
        <div className={classnames(styles.headerWrapper)}>
          <RichText {...headingRichTextProps} />
          {legacyHeaderButton}
        </div>
        <div className={styles.productsListContainer}>{slides}</div>
      </div>
    </div>
  )
}

export const FeaturedProductsShowcaseWidget: PLTypedWidgetInterface<
  FeaturedProductsShowcaseWidgetData,
  WidgetType.FEATURED_PRODUCTS_SHOWCASE
> = {
  id: WidgetType.FEATURED_PRODUCTS_SHOWCASE,
  errorBoundary: PLBaseErrorBoundary,
  render: FeaturedProductsShowcaseWidgetComponent,
}
